import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ActivityTypeService } from '../../activities/activity-type.service';
import { ActivityType, ScheduleType } from '../../activities/activity-type.model';
import { describeGenericApiError, extractApiErrorMessage } from '../../../core/http/api-error.util';
import { CreateTaskRequest, Task, isRecurring } from '../../tasks/task.model';
import { TaskService } from '../../tasks/task.service';
import { TimerOverlayComponent } from '../../timer/timer-overlay.component';
import { DayBlock, PutDayAllocationInput } from '../day.model';
import { DayService } from '../day.service';
import { TimeBlockService } from '../time-block.service';
import { LucideArrowLeft, LucideBan, LucideChevronDown, LucideChevronRight, LucideChevronUp, LucideUndo2 } from '@lucide/angular';
import { Subscription } from 'rxjs';
import { StartTimeBlockRequest } from '../time-block.model';
import { DayStore } from '../day.store';
import {
  SCHEDULE_GROUP_ORDER,
  getEnabledScheduleGroups,
  groupPageCommands,
  resolveScheduleGroupLabel,
  resolveScheduleTypeLabel,
  scheduleTypeFromSlug
} from '../schedule-group.util';
import { MeService } from '../../settings/me.service';
import { ScheduleGroup } from '../../settings/me.model';
import { isDoneThisCycle, resolveCycleDisplayLabel, resolveScheduleGroupCycle } from '../day-status-cycles.util';
import { nextOpenGroupActivityTypeId } from '../group-page.util';
import {
  MONTH_DAYS,
  formatRecurrenceDays,
  initialRecurrenceDays,
  recurrenceDaysError
} from '../task-recurrence.util';

/**
 * Component for displaying the details of an allocation for a specific activity type on the current day.
 * This includes the current allocation status, any running time block, and associated tasks.
 *
 * @author sadatmalik
 */
@Component({
  selector: 'app-allocation-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TimerOverlayComponent,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    LucideArrowLeft,
    LucideBan,
    LucideChevronDown,
    LucideChevronRight,
    LucideChevronUp,
    LucideUndo2
  ],
  templateUrl: './allocation-detail.component.html',
  styleUrls: ['./allocation-detail.component.scss']
})
export class AllocationDetailComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dayStore = inject(DayStore);
  private readonly activityTypeService = inject(ActivityTypeService);
  private readonly dayService = inject(DayService);
  private readonly timeBlockService = inject(TimeBlockService);
  private readonly taskService = inject(TaskService);
  private readonly meService = inject(MeService);
  private routeParamSubscription: Subscription | null = null;
  private routeQueryParamSubscription: Subscription | null = null;

  readonly activityTypeId = signal<string>(this.route.snapshot.paramMap.get('activityTypeId') ?? '');
  readonly fromScheduleType = signal<ScheduleType>(this.resolveScheduleType(this.route.snapshot.queryParamMap.get('from')));
  readonly activityTypes = signal<ActivityType[]>([]);
  readonly day = this.dayStore.day;
  readonly tasks = signal<Task[]>([]);
  readonly completedTasks = signal<Task[]>([]);

  readonly loading = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly taskError = signal<string | null>(null);
  readonly blockMutationError = signal<string | null>(null);

  readonly taskCreating = signal(false);
  readonly taskCompletingIds = signal<Record<string, boolean>>({});
  readonly taskReopeningIds = signal<Record<string, boolean>>({});
  readonly taskFocusingIds = signal<Record<string, boolean>>({});
  readonly taskPrioritizingIds = signal<Record<string, boolean>>({});
  readonly taskDeletingIds = signal<Record<string, boolean>>({});
  readonly taskMovingIds = signal<Record<string, boolean>>({});
  readonly taskReorderSubmitting = signal(false);
  readonly seriesCancellingIds = signal<Record<string, boolean>>({});
  readonly deleteChoiceByTaskId = signal<Record<string, boolean>>({});
  readonly completedExpanded = signal(false);
  readonly completedTasksListId = 'allocation-completed-tasks';
  readonly blockMutationSubmitting = signal(false);
  readonly startSubmitting = signal(false);
  readonly slotDraft = signal(0);
  readonly slotSaving = signal(false);
  readonly slotSaveError = signal<string | null>(null);
  readonly slotSaveSuccess = signal<string | null>(null);
  readonly nowTarget = signal<number | null>(null);
  readonly scheduleGroups = signal<ScheduleGroup[]>(this.defaultScheduleGroups());
  readonly groupingSubmitting = signal(false);
  readonly groupingError = signal<string | null>(null);
  readonly notThisCycleSubmitting = signal(false);
  readonly notThisCycleError = signal<string | null>(null);
  readonly resolveScheduleGroupLabel = resolveScheduleGroupLabel;
  readonly workCycleStartedAt = this.dayStore.workCycleStartedAt;
  readonly lifeCycleStartedAt = this.dayStore.lifeCycleStartedAt;
  readonly workCycleLabel = this.dayStore.workCycleLabel;
  readonly lifeCycleLabel = this.dayStore.lifeCycleLabel;

  newTaskTitle = '';
  readonly newTaskRecurrenceType = signal<'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY'>('NONE');
  readonly newTaskRecurrenceDays = signal<number[]>([]);
  readonly monthDays = MONTH_DAYS;

  readonly activityType = computed(() => this.activityTypes().find(
    (activity) => activity.id === this.activityTypeId()
  ) ?? null);

  readonly allocation = computed(() => this.day()?.allocations.find(
    (entry) => entry.activityTypeId === this.activityTypeId()
  ) ?? null);

  readonly runningBlock = computed<DayBlock | null>(() => {
    const runningBlock = this.day()?.runningBlock;
    if (!runningBlock || runningBlock.activityTypeId !== this.activityTypeId()) {
      return null;
    }
    return runningBlock;
  });
  readonly pausedBlock = computed<DayBlock | null>(() => {
    const pausedBlocks = this.day()?.pausedBlocks ?? [];
    return pausedBlocks.find((block) => block.activityTypeId === this.activityTypeId()) ?? null;
  });

  readonly isRunning = computed(() => !!this.runningBlock());
  readonly canResumePausedBlock = computed(() => !!this.pausedBlock() && !this.day()?.runningBlock);
  readonly hasFocusToday = computed(() => (this.allocation()?.focusedTaskCount ?? 0) > 0);
  readonly hasPriorityToday = computed(() => (this.allocation()?.prioritizedTaskCount ?? 0) > 0);
  readonly isCompleted = computed(() => {
    const allocation = this.allocation();
    return (allocation?.slotCount ?? 0) > 0
      && (allocation?.completedSlots ?? 0) >= (allocation?.slotCount ?? 0);
  });
  readonly enabledScheduleGroups = computed(() => getEnabledScheduleGroups(this.scheduleGroups()));
  readonly backScheduleType = computed(() => this.activityType()?.scheduleType ?? this.fromScheduleType());
  readonly backGroupLabel = computed(() => resolveScheduleTypeLabel(this.backScheduleType(), this.scheduleGroups()));
  readonly backLabel = computed(() => `Back to ${this.backGroupLabel()}`);
  readonly backCommands = computed(() => groupPageCommands(this.backScheduleType()));
  readonly moveTargets = computed(() => {
    const currentScheduleType = this.activityType()?.scheduleType ?? this.fromScheduleType();
    return this.enabledScheduleGroups().filter((group) => group.ordinal !== currentScheduleType);
  });

  readonly activityCycle = computed(() => {
    const scheduleType = this.activityType()?.scheduleType ?? this.fromScheduleType();
    const group = this.scheduleGroups().find((candidate) => candidate.ordinal === scheduleType);
    return resolveScheduleGroupCycle(group);
  });

  readonly activityCycleStartedAt = computed(() => (
    this.activityCycle() === 'LIFE' ? this.lifeCycleStartedAt() : this.workCycleStartedAt()
  ));

  readonly isNotThisCycle = computed(() => (
    isDoneThisCycle(this.activityType()?.notThisCycleAt, this.activityCycleStartedAt())
  ));

  readonly setAsideMetaLabel = computed(() => (
    `Set aside until the ${resolveCycleDisplayLabel(this.activityCycle(), this.workCycleLabel(), this.lifeCycleLabel())} cycle resets`
  ));

  readonly slotProgressLabel = computed(() => {
    const allocation = this.allocation();
    if (!allocation || allocation.slotCount <= 0) {
      return 'No planned slots';
    }
    if (this.isRunning()) {
      const slotIndex = Math.min(allocation.slotCount, allocation.completedSlots + 1);
      return `${slotIndex} / ${allocation.slotCount}`;
    }
    return `${allocation.completedSlots} / ${allocation.slotCount} slots`;
  });
  readonly slotDurationMinutes = computed(() => (
    this.activityType()?.defaultMinutes ?? this.allocation()?.minutesPerSlot ?? null
  ));
  readonly remainingSlots = computed(() => {
    const allocation = this.allocation();
    if (!allocation || allocation.slotCount <= 0) {
      return null;
    }
    return Math.max(0, allocation.slotCount - (allocation.completedSlots + 1));
  });

  readonly nowTasks = computed(() => {
    const todayDate = this.currentDayDate();
    return this.tasks().filter((task) => task.prioritizedDate === todayDate);
  });

  readonly nextTasks = computed(() => {
    const todayDate = this.currentDayDate();
    return this.tasks().filter((task) => (
      task.focusedDate === todayDate && task.prioritizedDate !== todayDate
    ));
  });

  readonly laterTasks = computed(() => {
    const todayDate = this.currentDayDate();
    const tasks = this.tasks().filter((task) => task.focusedDate !== todayDate);
    return [...tasks].sort((a, b) => {
      if (a.displayOrder !== b.displayOrder) {
        return a.displayOrder - b.displayOrder;
      }
      return a.createdAt.localeCompare(b.createdAt);
    });
  });
  readonly overNowTarget = computed(() => {
    const target = this.nowTarget();
    if (target === null) {
      return false;
    }
    return this.nowTasks().length > target;
  });
  readonly nextActivityTypeId = computed(() => {
    const rows = this.dayStore.getAllocationRowsForScheduleType(this.backScheduleType());
    return nextOpenGroupActivityTypeId(rows, this.activityTypeId(), this.dayStore.dayStatusOldestFirst());
  });
  readonly moveTaskTargets = computed(() => this.activityTypes()
    .filter((activity) => activity.kind === 'TIME_BLOCK' && activity.id !== this.activityTypeId())
    .sort((left, right) => left.displayOrder - right.displayOrder));
  constructor() {
    effect(() => {
      this.slotDraft.set(this.allocation()?.slotCount ?? 0);
    });
  }

  ngOnInit(): void {
    this.loadAll();
    this.routeParamSubscription = this.route.paramMap.subscribe((params) => {
      const nextActivityTypeId = params.get('activityTypeId') ?? '';
      if (nextActivityTypeId === this.activityTypeId()) {
        return;
      }
      this.activityTypeId.set(nextActivityTypeId);
      this.loadAll();
    });
    this.routeQueryParamSubscription = this.route.queryParamMap.subscribe((queryParams) => {
      this.fromScheduleType.set(this.resolveScheduleType(queryParams.get('from')));
    });
  }

  ngOnDestroy(): void {
    this.routeParamSubscription?.unsubscribe();
    this.routeQueryParamSubscription?.unsubscribe();
  }

  goBack(): void {
    this.router.navigate(this.backCommands());
  }

  refresh(): void {
    this.loadAll();
  }

  navigateToNext(): void {
    const nextActivityTypeId = this.nextActivityTypeId();
    if (!nextActivityTypeId) {
      return;
    }
    this.router.navigate(['/today/allocation', nextActivityTypeId], {
      queryParams: { from: this.backScheduleType() }
    });
  }

  openTask(task: Task): void {
    this.router.navigate(['/today/allocation', this.activityTypeId(), 'task', task.id], {
      queryParams: { from: this.backScheduleType() }
    });
  }

  onTaskGroupDrop(group: ReadonlyArray<Task>, event: CdkDragDrop<Task[]>): void {
    if (this.taskReorderSubmitting() || event.previousIndex === event.currentIndex || !this.activityTypeId()) {
      return;
    }
    const reorderedGroup = [...group];
    moveItemInArray(reorderedGroup, event.previousIndex, event.currentIndex);
    const previousTasks = this.tasks();
    const orderedIds = this.rebuildOrderedIdsForGroup(previousTasks, group, reorderedGroup);
    const optimisticTasks = this.applyOrderedIds(previousTasks, orderedIds);

    this.tasks.set(optimisticTasks);
    this.taskError.set(null);
    this.taskReorderSubmitting.set(true);

    this.taskService.reorder(orderedIds, this.activityTypeId()).subscribe({
      next: (updatedTasks) => {
        this.tasks.set(this.mergeTaskUpdates(this.tasks(), updatedTasks));
        this.taskReorderSubmitting.set(false);
      },
      error: (err: unknown) => {
        this.tasks.set(previousTasks);
        this.taskError.set(describeGenericApiError(err, 'Failed to reorder tasks'));
        this.taskReorderSubmitting.set(false);
      }
    });
  }

  moveTaskToActivityType(task: Task, targetActivityTypeId: string): void {
    if (!targetActivityTypeId || task.activityTypeId === targetActivityTypeId || this.isTaskBusy(task.id)) {
      return;
    }
    const previousTasks = this.tasks();
    this.tasks.set(previousTasks.filter((entry) => entry.id !== task.id));
    this.taskError.set(null);
    this.taskMovingIds.update((current) => ({ ...current, [task.id]: true }));

    this.taskService.moveToActivityType(task.id, targetActivityTypeId).subscribe({
      next: () => {
        this.clearMutationId(this.taskMovingIds, task.id);
      },
      error: (err: unknown) => {
        this.tasks.set(previousTasks);
        this.taskError.set(describeGenericApiError(err, 'Failed to move task'));
        this.clearMutationId(this.taskMovingIds, task.id);
      }
    });
  }

  isTaskBusy(taskId: string): boolean {
    return this.taskReorderSubmitting()
      || !!this.taskCompletingIds()[taskId]
      || !!this.taskReopeningIds()[taskId]
      || !!this.taskFocusingIds()[taskId]
      || !!this.taskPrioritizingIds()[taskId]
      || !!this.taskDeletingIds()[taskId]
      || !!this.seriesCancellingIds()[taskId]
      || !!this.taskMovingIds()[taskId];
  }

  getTaskItemCountLabel(task: Task): string | null {
    const total = task.totalItemCount ?? 0;
    if (total <= 0) {
      return null;
    }
    const open = task.openItemCount ?? 0;
    return `${open}/${total}`;
  }

  saveSlots(): void {
    const currentDay = this.day();
    if (!currentDay || this.slotSaving()) {
      return;
    }

    const fallbackMinutesPerSlot = currentDay.allocations[0]?.minutesPerSlot
      ?? this.activityType()?.defaultMinutes
      ?? this.allocation()?.minutesPerSlot
      ?? null;
    if (fallbackMinutesPerSlot === null || fallbackMinutesPerSlot <= 0) {
      this.slotSaveError.set('Unable to resolve slot duration. Set a default duration first.');
      return;
    }

    const normalizedSlotCount = Math.max(0, Math.floor(this.slotDraft()));
    const allocationById = new Map(currentDay.allocations.map((entry) => [entry.activityTypeId, entry]));
    allocationById.set(this.activityTypeId(), {
      activityTypeId: this.activityTypeId(),
      slotCount: normalizedSlotCount,
      minutesPerSlot: this.allocation()?.minutesPerSlot ?? fallbackMinutesPerSlot,
      completedSlots: this.allocation()?.completedSlots ?? 0,
      scheduleType: this.fromScheduleType(),
      hiddenToday: this.allocation()?.hiddenToday ?? false
    });

    const allocations: PutDayAllocationInput[] = Array.from(allocationById.values())
      .filter((entry) => entry.slotCount > 0)
      .map((entry) => ({
        activityTypeId: entry.activityTypeId,
        slotCount: entry.slotCount,
        minutesPerSlot: entry.minutesPerSlot
      }));

    this.slotSaving.set(true);
    this.slotSaveError.set(null);
    this.slotSaveSuccess.set(null);
    this.dayService.putAllocations(currentDay.dayDate, {
      allocations,
      version: currentDay.allocationsVersion
    }).subscribe({
      next: (response) => {
        this.day.update((day) => day
          ? { ...day, allocationsVersion: response.version }
          : day);
        this.slotSaveSuccess.set('Slots saved.');
        this.loadDay();
        this.slotSaving.set(false);
      },
      error: (err: unknown) => {
        this.slotSaveError.set(err instanceof HttpErrorResponse && err.status === 412
          ? 'Allocations changed in another session. Refreshed with latest values.'
          : describeGenericApiError(err, 'Failed to save slots'));
        this.loadDay();
        this.slotSaving.set(false);
      }
    });
  }

  setSlotDraft(value: string | number): void {
    const parsedValue = typeof value === 'number' ? value : Number(value);
    this.slotDraft.set(Number.isFinite(parsedValue) ? Math.max(0, Math.floor(parsedValue)) : 0);
  }

  moveToScheduleType(target: ScheduleType): void {
    const activity = this.activityType();
    if (!activity || activity.kind !== 'TIME_BLOCK' || activity.scheduleType === target || this.groupingSubmitting()) {
      return;
    }

    const previousActivities = this.activityTypes();
    const previousDay = this.day();
    const targetActivities = previousActivities
      .filter((current) => current.kind === 'TIME_BLOCK' && current.scheduleType === target);
    const nextDisplayOrder = targetActivities.length === 0
      ? 0
      : Math.min(...targetActivities.map((current) => current.displayOrder)) - 1;

    this.activityTypes.set(previousActivities.map((current) => current.id === activity.id
      ? { ...current, scheduleType: target, displayOrder: nextDisplayOrder }
      : current));
    this.day.update((current) => {
      if (!current) {
        return current;
      }
      return {
        ...current,
        allocations: current.allocations.map((allocation) => allocation.activityTypeId === activity.id
          ? { ...allocation, scheduleType: target }
          : allocation)
      };
    });
    this.fromScheduleType.set(target);
    this.groupingSubmitting.set(true);
    this.groupingError.set(null);

    this.activityTypeService.moveToScheduleType(activity.id, target).subscribe({
      next: (updatedActivity) => {
        this.activityTypes.update((current) => current.map((entry) => entry.id === updatedActivity.id
          ? updatedActivity
          : entry));
        this.fromScheduleType.set(updatedActivity.scheduleType);
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { from: updatedActivity.scheduleType },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
        this.loadDay();
        this.groupingSubmitting.set(false);
      },
      error: (err: unknown) => {
        this.activityTypes.set(previousActivities);
        this.day.set(previousDay);
        this.fromScheduleType.set(activity.scheduleType);
        this.groupingError.set(describeGenericApiError(err, 'Failed to move allocation'));
        this.groupingSubmitting.set(false);
      }
    });
  }

  markNotThisCycle(): void {
    const activity = this.activityType();
    if (!activity || this.isRunning() || this.notThisCycleSubmitting()) {
      return;
    }
    if (!this.dayStore.confirmNotThisCycle(activity.id)) {
      return;
    }
    this.notThisCycleSubmitting.set(true);
    this.notThisCycleError.set(null);
    this.activityTypeService.markNotThisCycle(activity.id).subscribe({
      next: () => {
        this.loadActivityTypes();
        this.loadDay();
        this.dayStore.reloadAfterCycleChange();
        this.notThisCycleSubmitting.set(false);
      },
      error: (err: unknown) => {
        this.notThisCycleError.set(describeGenericApiError(err, 'Failed to set activity aside for this cycle'));
        this.notThisCycleSubmitting.set(false);
      }
    });
  }

  clearNotThisCycle(): void {
    const activity = this.activityType();
    if (!activity || this.notThisCycleSubmitting()) {
      return;
    }
    this.notThisCycleSubmitting.set(true);
    this.notThisCycleError.set(null);
    this.activityTypeService.clearNotThisCycle(activity.id).subscribe({
      next: () => {
        this.loadActivityTypes();
        this.loadDay();
        this.dayStore.reloadAfterCycleChange();
        this.notThisCycleSubmitting.set(false);
      },
      error: (err: unknown) => {
        this.notThisCycleError.set(describeGenericApiError(err, 'Failed to undo set aside'));
        this.notThisCycleSubmitting.set(false);
      }
    });
  }

  startBlock(): void {
    const currentDay = this.day();
    const activity = this.activityType();
    if (!currentDay || !activity || this.startSubmitting() || this.blockMutationSubmitting()) {
      return;
    }
    if (this.pausedBlock()) {
      this.blockMutationError.set('A paused block already exists for this allocation. Resume it instead.');
      return;
    }
    if (currentDay.runningBlock) {
      this.blockMutationError.set('Another running block already exists.');
      return;
    }

    const allocation = this.allocation();
    const durationMinutes = activity.defaultMinutes ?? allocation?.minutesPerSlot ?? null;
    if (durationMinutes === null || durationMinutes <= 0) {
      this.blockMutationError.set('Cannot start block: duration minutes could not be resolved.');
      return;
    }

    this.startSubmitting.set(true);
    this.blockMutationError.set(null);

    this.timeBlockService.start({
      dayDate: currentDay.dayDate,
      activityTypeId: activity.id,
      durationMinutes,
      source: allocation && allocation.slotCount > 0 ? 'ALLOCATED' : 'ADHOC'
    }).subscribe({
      next: () => {
        this.loadDay();
        this.startSubmitting.set(false);
      },
      error: (err: unknown) => {
        this.blockMutationError.set(this.describeTimeBlockMutationError(err, 'start'));
        this.startSubmitting.set(false);
      }
    });
  }

  completeRunningBlock(): void {
    const runningBlock = this.runningBlock();
    if (!runningBlock || this.blockMutationSubmitting()) {
      return;
    }
    this.blockMutationSubmitting.set(true);
    this.blockMutationError.set(null);

    this.timeBlockService.complete(runningBlock.id, { endedAt: new Date().toISOString() }).subscribe({
      next: () => {
        this.loadDay();
        this.blockMutationSubmitting.set(false);
      },
      error: (err: unknown) => {
        this.blockMutationError.set(this.describeTimeBlockMutationError(err, 'complete'));
        this.blockMutationSubmitting.set(false);
      }
    });
  }

  onAutoAdvance(): void {
    const runningBlock = this.runningBlock();
    const currentDay = this.day();
    const allocation = this.allocation();
    if (!runningBlock || !currentDay || !allocation || this.blockMutationSubmitting()) {
      return;
    }
    const remainingSlots = Math.max(0, allocation.slotCount - (allocation.completedSlots + 1));
    if (remainingSlots <= 0) {
      return;
    }

    const durationMinutes = runningBlock.durationMinutes;
    if (!durationMinutes || durationMinutes <= 0) {
      this.blockMutationError.set('Cannot auto-advance: duration minutes could not be resolved.');
      return;
    }

    const startRequest: StartTimeBlockRequest = {
      dayDate: currentDay.dayDate,
      activityTypeId: runningBlock.activityTypeId,
      durationMinutes,
      source: 'ALLOCATED'
    };

    this.blockMutationSubmitting.set(true);
    this.blockMutationError.set(null);
    this.timeBlockService.complete(runningBlock.id, { endedAt: new Date().toISOString() }).subscribe({
      next: () => {
        this.timeBlockService.start(startRequest).subscribe({
          next: () => {
            this.loadDay();
            this.blockMutationSubmitting.set(false);
          },
          error: (err: unknown) => {
            this.blockMutationError.set(this.describeTimeBlockMutationError(err, 'start'));
            this.loadDay();
            this.blockMutationSubmitting.set(false);
          }
        });
      },
      error: (err: unknown) => {
        this.blockMutationError.set(this.describeTimeBlockMutationError(err, 'complete'));
        this.blockMutationSubmitting.set(false);
      }
    });
  }

  skipRunningBlock(reason: string): void {
    const runningBlock = this.runningBlock();
    if (!runningBlock || this.blockMutationSubmitting()) {
      return;
    }
    this.blockMutationSubmitting.set(true);
    this.blockMutationError.set(null);

    this.timeBlockService.skip(runningBlock.id, {
      endedAt: new Date().toISOString(),
      reason: reason.trim() || 'Skipped'
    }).subscribe({
      next: () => {
        this.loadDay();
        this.blockMutationSubmitting.set(false);
      },
      error: (err: unknown) => {
        this.blockMutationError.set(this.describeTimeBlockMutationError(err, 'skip'));
        this.blockMutationSubmitting.set(false);
      }
    });
  }

  pauseRunningBlock(): void {
    const runningBlock = this.runningBlock();
    if (!runningBlock || runningBlock.pausedAt || this.blockMutationSubmitting()) {
      return;
    }
    this.blockMutationSubmitting.set(true);
    this.blockMutationError.set(null);

    this.timeBlockService.pause(runningBlock.id).subscribe({
      next: () => {
        this.loadDay();
        this.blockMutationSubmitting.set(false);
      },
      error: (err: unknown) => {
        this.blockMutationError.set(this.describeTimeBlockMutationError(err, 'pause'));
        this.blockMutationSubmitting.set(false);
      }
    });
  }

  resumeRunningBlock(): void {
    const runningBlock = this.runningBlock();
    if (!runningBlock || !runningBlock.pausedAt || this.blockMutationSubmitting()) {
      return;
    }
    this.blockMutationSubmitting.set(true);
    this.blockMutationError.set(null);

    this.timeBlockService.resume(runningBlock.id).subscribe({
      next: () => {
        this.loadDay();
        this.blockMutationSubmitting.set(false);
      },
      error: (err: unknown) => {
        this.blockMutationError.set(this.describeTimeBlockMutationError(err, 'resume'));
        this.blockMutationSubmitting.set(false);
      }
    });
  }

  resumePausedBlock(): void {
    const pausedBlock = this.pausedBlock();
    const currentDay = this.day();
    if (!pausedBlock || !currentDay || currentDay.runningBlock || this.blockMutationSubmitting()) {
      return;
    }
    this.blockMutationSubmitting.set(true);
    this.blockMutationError.set(null);

    this.timeBlockService.resume(pausedBlock.id).subscribe({
      next: () => {
        this.loadDay();
        this.blockMutationSubmitting.set(false);
      },
      error: (err: unknown) => {
        this.blockMutationError.set(this.describeTimeBlockMutationError(err, 'resume'));
        this.blockMutationSubmitting.set(false);
      }
    });
  }

  createTask(): void {
    const title = this.newTaskTitle.trim();
    if (!title || this.taskCreating() || !this.activityTypeId()) {
      return;
    }

    const recurrenceType = this.newTaskRecurrenceType();
    const recurrenceDays = this.newTaskRecurrenceDays();
    const daysError = recurrenceDaysError(recurrenceType, recurrenceDays);
    if (daysError) {
      this.taskError.set(daysError);
      return;
    }

    const request: CreateTaskRequest = {
      activityTypeId: this.activityTypeId(),
      title
    };
    if (recurrenceType !== 'NONE') {
      request.recurrenceType = recurrenceType;
      request.recurrenceInterval = 1;
      const formattedDays = formatRecurrenceDays(recurrenceType, recurrenceDays);
      if (formattedDays) {
        request.recurrenceDays = formattedDays;
      }
    }

    this.taskCreating.set(true);
    this.taskError.set(null);
    this.taskService.create(request).subscribe({
      next: (created) => {
        this.tasks.update(current => [...current, created]);
        this.newTaskTitle = '';
        this.newTaskRecurrenceType.set('NONE');
        this.newTaskRecurrenceDays.set([]);
        this.taskCreating.set(false);
      },
      error: (err: unknown) => {
        this.taskError.set(describeGenericApiError(err, 'Failed to create task'));
        this.taskCreating.set(false);
      }
    });
  }

  completeTask(task: Task): void {
    if (this.taskCompletingIds()[task.id]) {
      return;
    }

    const previousTasks = this.tasks();
    const previousCompletedTasks = this.completedTasks();
    const optimisticCompletedTask: Task = {
      ...task,
      completedAt: new Date().toISOString()
    };
    this.tasks.set(previousTasks.filter((entry) => entry.id !== task.id));
    this.completedTasks.set([optimisticCompletedTask, ...previousCompletedTasks]);
    this.taskError.set(null);
    this.taskCompletingIds.update((current) => ({ ...current, [task.id]: true }));

    this.taskService.complete(task.id).subscribe({
      next: (completedTask) => {
        this.completedTasks.update((current) => [
          completedTask,
          ...current.filter((entry) => entry.id !== task.id)
        ]);
        this.clearMutationId(this.taskCompletingIds, task.id);
      },
      error: (err: unknown) => {
        this.tasks.set(previousTasks);
        this.completedTasks.set(previousCompletedTasks);
        this.taskError.set(describeGenericApiError(err, 'Failed to complete task'));
        this.clearMutationId(this.taskCompletingIds, task.id);
      }
    });
  }

  focusTask(task: Task): void {
    if (this.taskFocusingIds()[task.id]) {
      return;
    }

    const previousTasks = this.tasks();
    const todayDate = this.currentDayDate();
    this.tasks.update((current) => current.map((entry) => entry.id === task.id
      ? { ...entry, focusedDate: todayDate }
      : entry));
    this.taskError.set(null);
    this.taskFocusingIds.update((current) => ({ ...current, [task.id]: true }));

    this.taskService.focus(task.id).subscribe({
      next: (focusedTask) => {
        this.tasks.update((current) => current.map((entry) => entry.id === task.id ? focusedTask : entry));
        this.clearMutationId(this.taskFocusingIds, task.id);
      },
      error: (err: unknown) => {
        this.tasks.set(previousTasks);
        this.taskError.set(describeGenericApiError(err, 'Failed to focus task'));
        this.clearMutationId(this.taskFocusingIds, task.id);
      }
    });
  }

  unfocusTask(task: Task): void {
    if (this.taskFocusingIds()[task.id]) {
      return;
    }

    const previousTasks = this.tasks();
    this.tasks.update((current) => current.map((entry) => entry.id === task.id
      ? { ...entry, focusedDate: null }
      : entry));
    this.taskError.set(null);
    this.taskFocusingIds.update((current) => ({ ...current, [task.id]: true }));

    this.taskService.unfocus(task.id).subscribe({
      next: (unfocusedTask) => {
        this.tasks.update((current) => current.map((entry) => entry.id === task.id ? unfocusedTask : entry));
        this.clearMutationId(this.taskFocusingIds, task.id);
      },
      error: (err: unknown) => {
        this.tasks.set(previousTasks);
        this.taskError.set(describeGenericApiError(err, 'Failed to unfocus task'));
        this.clearMutationId(this.taskFocusingIds, task.id);
      }
    });
  }

  prioritizeTask(task: Task): void {
    if (this.taskPrioritizingIds()[task.id]) {
      return;
    }

    const previousTasks = this.tasks();
    const todayDate = this.currentDayDate();
    this.tasks.update((current) => current.map((entry) => entry.id === task.id
      ? { ...entry, prioritizedDate: todayDate }
      : entry));
    this.taskError.set(null);
    this.taskPrioritizingIds.update((current) => ({ ...current, [task.id]: true }));

    this.taskService.prioritize(task.id).subscribe({
      next: (prioritizedTask) => {
        this.tasks.update((current) => current.map((entry) => entry.id === task.id ? prioritizedTask : entry));
        this.clearMutationId(this.taskPrioritizingIds, task.id);
        this.loadDay();
      },
      error: (err: unknown) => {
        this.tasks.set(previousTasks);
        this.taskError.set(describeGenericApiError(err, 'Failed to prioritize task'));
        this.clearMutationId(this.taskPrioritizingIds, task.id);
      }
    });
  }

  unprioritizeTask(task: Task): void {
    if (this.taskPrioritizingIds()[task.id]) {
      return;
    }

    const previousTasks = this.tasks();
    this.tasks.update((current) => current.map((entry) => entry.id === task.id
      ? { ...entry, prioritizedDate: null }
      : entry));
    this.taskError.set(null);
    this.taskPrioritizingIds.update((current) => ({ ...current, [task.id]: true }));

    this.taskService.unprioritize(task.id).subscribe({
      next: (unprioritizedTask) => {
        this.tasks.update((current) => current.map((entry) => entry.id === task.id ? unprioritizedTask : entry));
        this.clearMutationId(this.taskPrioritizingIds, task.id);
        this.loadDay();
      },
      error: (err: unknown) => {
        this.tasks.set(previousTasks);
        this.taskError.set(describeGenericApiError(err, 'Failed to remove task from priorities'));
        this.clearMutationId(this.taskPrioritizingIds, task.id);
      }
    });
  }

  reopenTask(task: Task): void {
    if (this.taskReopeningIds()[task.id]) {
      return;
    }

    const previousTasks = this.tasks();
    const previousCompletedTasks = this.completedTasks();
    const reopenedTask: Task = {
      ...task,
      completedAt: null
    };
    this.completedTasks.set(previousCompletedTasks.filter((entry) => entry.id !== task.id));
    this.tasks.set([...previousTasks, reopenedTask]);
    this.taskError.set(null);
    this.taskReopeningIds.update((current) => ({ ...current, [task.id]: true }));

    this.taskService.reopen(task.id).subscribe({
      next: (updatedTask) => {
        this.tasks.update((current) => current.map((entry) => entry.id === task.id ? updatedTask : entry));
        this.clearMutationId(this.taskReopeningIds, task.id);
      },
      error: (err: unknown) => {
        this.tasks.set(previousTasks);
        this.completedTasks.set(previousCompletedTasks);
        this.taskError.set(describeGenericApiError(err, 'Failed to reopen task'));
        this.clearMutationId(this.taskReopeningIds, task.id);
      }
    });
  }

  setNewTaskRecurrenceType(value: string): void {
    const nextType: 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY' = (
      value === 'DAILY' || value === 'WEEKLY' || value === 'MONTHLY'
    ) ? value : 'NONE';
    if (nextType === this.newTaskRecurrenceType()) {
      return;
    }
    this.newTaskRecurrenceType.set(nextType);
    this.newTaskRecurrenceDays.set(initialRecurrenceDays(nextType, this.dayStore.day()?.dayDate));
  }

  toggleWeeklyDay(day: number): void {
    if (this.newTaskRecurrenceType() !== 'WEEKLY') {
      return;
    }
    this.newTaskRecurrenceDays.update((current) => (
      current.includes(day)
        ? current.filter((entry) => entry !== day)
        : [...current, day]
    ));
  }

  isWeeklyDaySelected(day: number): boolean {
    return this.newTaskRecurrenceDays().includes(day);
  }

  toggleMonthlyDay(day: number): void {
    if (this.newTaskRecurrenceType() !== 'MONTHLY') {
      return;
    }
    this.newTaskRecurrenceDays.update((current) => (
      current.includes(day)
        ? current.filter((entry) => entry !== day)
        : [...current, day]
    ));
  }

  isMonthlyDaySelected(day: number): boolean {
    return this.newTaskRecurrenceType() === 'MONTHLY' && this.newTaskRecurrenceDays().includes(day);
  }

  isRecurringTask(task: Task): boolean {
    return isRecurring(task);
  }

  isTaskPrioritizedToday(task: Task): boolean {
    return task.prioritizedDate === this.currentDayDate();
  }

  showDeleteChoices(taskId: string): void {
    this.deleteChoiceByTaskId.update((current) => ({ ...current, [taskId]: true }));
  }

  hideDeleteChoices(taskId: string): void {
    this.deleteChoiceByTaskId.update((current) => {
      const { [taskId]: _removed, ...rest } = current;
      return rest;
    });
  }

  hasDeleteChoices(taskId: string): boolean {
    return !!this.deleteChoiceByTaskId()[taskId];
  }

  deleteTask(task: Task): void {
    if (this.taskDeletingIds()[task.id] || this.seriesCancellingIds()[task.id]) {
      return;
    }

    const previousTasks = this.tasks();
    const previousCompleted = this.completedTasks();
    this.tasks.set(previousTasks.filter((entry) => entry.id !== task.id));
    this.completedTasks.set(previousCompleted.filter((entry) => entry.id !== task.id));
    this.taskError.set(null);
    this.taskDeletingIds.update((current) => ({ ...current, [task.id]: true }));
    this.hideDeleteChoices(task.id);

    this.taskService.delete(task.id).subscribe({
      next: () => {
        this.clearMutationId(this.taskDeletingIds, task.id);
      },
      error: (err: unknown) => {
        this.tasks.set(previousTasks);
        this.completedTasks.set(previousCompleted);
        this.taskError.set(describeGenericApiError(err, 'Failed to delete task'));
        this.clearMutationId(this.taskDeletingIds, task.id);
      }
    });
  }

  deleteTaskSeries(task: Task): void {
    const seriesId = task.parentTaskId ?? task.id;
    if (!seriesId || this.seriesCancellingIds()[seriesId]) {
      return;
    }

    const previousTasks = this.tasks();
    const previousCompleted = this.completedTasks();
    this.tasks.set(previousTasks.filter((entry) => (entry.parentTaskId ?? entry.id) !== seriesId));
    this.completedTasks.set(previousCompleted.filter((entry) => (entry.parentTaskId ?? entry.id) !== seriesId));
    this.taskError.set(null);
    this.seriesCancellingIds.update((current) => ({ ...current, [seriesId]: true }));
    this.hideDeleteChoices(task.id);

    this.taskService.cancelSeries(task.id).subscribe({
      next: () => {
        this.clearMutationId(this.seriesCancellingIds, seriesId);
      },
      error: (err: unknown) => {
        this.tasks.set(previousTasks);
        this.completedTasks.set(previousCompleted);
        this.taskError.set(describeGenericApiError(err, 'Failed to cancel recurring series'));
        this.clearMutationId(this.seriesCancellingIds, seriesId);
      }
    });
  }

  toggleCompletedSection(): void {
    this.completedExpanded.update((current) => !current);
  }

  private loadAll(): void {
    this.loadActivityTypes();
    this.loadDay(false);
    this.loadTasks();
    this.loadCompletedTasks();
    this.loadTarget();
  }

  private loadActivityTypes(): void {
    this.activityTypeService.list().subscribe({
      next: (activityTypes) => this.activityTypes.set(activityTypes),
      error: (err: unknown) => {
        this.loadError.set(describeGenericApiError(err, 'Failed to load activity types'));
      }
    });
  }

  private loadDay(forceRefresh = true): void {
    if (!forceRefresh && this.dayStore.hasFreshDayData()) {
      this.loading.set(false);
      this.loadError.set(null);
      return;
    }
    this.loading.set(true);
    this.loadError.set(null);
    const request$ = forceRefresh
      ? this.dayStore.refresh({ silent: true })
      : this.dayStore.ensureLoaded();
    request$.subscribe({
      next: () => {
        if (!this.day()) {
          this.loadError.set(this.dayStore.dayError() ?? 'Failed to load allocation detail');
        }
        this.loading.set(false);
      }
    });
  }

  private loadTasks(): void {
    this.taskService.listActive(this.activityTypeId()).subscribe({
      next: (tasks) => this.tasks.set(tasks),
      error: (err: unknown) => {
        this.taskError.set(describeGenericApiError(err, 'Failed to load tasks'));
      }
    });
  }

  private loadCompletedTasks(): void {
    this.taskService.listCompleted(this.activityTypeId()).subscribe({
      next: (tasks) => this.completedTasks.set(tasks),
      error: (err: unknown) => {
        this.taskError.set(describeGenericApiError(err, 'Failed to load completed tasks'));
      }
    });
  }

  private loadTarget(): void {
    this.meService.getMe().subscribe({
      next: (response) => {
        this.nowTarget.set(response.settings?.priorityDailyTarget ?? null);
        this.scheduleGroups.set(this.normalizeScheduleGroups(
          response.settings?.scheduleGroups ?? [],
          response.settings?.scheduleGroupCount
        ));
      },
      error: () => {
        this.nowTarget.set(null);
      }
    });
  }

  private defaultScheduleGroups(): ScheduleGroup[] {
    return SCHEDULE_GROUP_ORDER.map((ordinal, index) => ({
      ordinal,
      label: null,
      targetEndLocalTime: null,
      budgetMinutes: null,
      position: index,
      cycle: 'WORK',
      enabled: index < 2
    }));
  }

  private normalizeScheduleGroups(rawGroups: ScheduleGroup[], groupCount?: number): ScheduleGroup[] {
    const enabledCount = Number.isFinite(groupCount) ? Math.max(1, Math.min(5, Number(groupCount))) : 2;
    return SCHEDULE_GROUP_ORDER.map((ordinal, index) => {
      const existing = rawGroups.find((group) => group.ordinal === ordinal);
      return {
        ordinal,
        label: existing?.label ?? null,
        targetEndLocalTime: existing?.targetEndLocalTime ?? null,
        budgetMinutes: existing?.budgetMinutes ?? null,
        position: this.normalizeScheduleGroupPosition(existing?.position, index),
        cycle: existing?.cycle === 'LIFE' ? 'LIFE' : 'WORK',
        enabled: existing?.enabled ?? index < enabledCount
      };
    });
  }

  private normalizeScheduleGroupPosition(position: number | null | undefined, fallback: number): number {
    if (!Number.isFinite(position)) {
      return fallback;
    }
    return Math.max(0, Math.min(4, Math.floor(Number(position))));
  }

  private rebuildOrderedIdsForGroup(
    allTasks: ReadonlyArray<Task>,
    previousGroup: ReadonlyArray<Task>,
    reorderedGroup: ReadonlyArray<Task>
  ): string[] {
    const canonical = this.sortByDisplayOrder(allTasks);
    const previousGroupIds = new Set(previousGroup.map((task) => task.id));
    const reorderedIds = reorderedGroup.map((task) => task.id);
    let reorderedIndex = 0;
    return canonical.map((task) => (
      previousGroupIds.has(task.id)
        ? reorderedIds[reorderedIndex++]
        : task.id
    ));
  }

  private applyOrderedIds(allTasks: ReadonlyArray<Task>, orderedIds: ReadonlyArray<string>): Task[] {
    const byId = new Map(allTasks.map((task) => [task.id, task] as const));
    const reordered = orderedIds.map((id, index) => {
      const task = byId.get(id);
      if (!task) {
        return null;
      }
      return { ...task, displayOrder: index };
    }).filter((task): task is Task => task !== null);

    const orderedIdSet = new Set(orderedIds);
    const untouched = allTasks.filter((task) => !orderedIdSet.has(task.id));
    return [...reordered, ...untouched];
  }

  private mergeTaskUpdates(currentTasks: ReadonlyArray<Task>, updates: ReadonlyArray<Task>): Task[] {
    const updateMap = new Map(updates.map((task) => [task.id, task] as const));
    return currentTasks.map((task) => {
      const updated = updateMap.get(task.id);
      if (!updated) {
        return task;
      }
      return {
        ...updated,
        openItemCount: task.openItemCount ?? updated.openItemCount,
        totalItemCount: task.totalItemCount ?? updated.totalItemCount
      };
    });
  }

  private sortByDisplayOrder(tasks: ReadonlyArray<Task>): Task[] {
    return [...tasks].sort((left, right) => {
      if (left.displayOrder !== right.displayOrder) {
        return left.displayOrder - right.displayOrder;
      }
      return left.createdAt.localeCompare(right.createdAt);
    });
  }

  private currentDayDate(): string {
    return this.day()?.dayDate ?? new Date().toISOString().split('T')[0];
  }

  private clearMutationId(
    mutationState: { update: (updater: (current: Record<string, boolean>) => Record<string, boolean>) => void },
    taskId: string
  ): void {
    mutationState.update((current) => {
      const { [taskId]: _removed, ...rest } = current;
      return rest;
    });
  }

  private describeTimeBlockMutationError(
    err: unknown,
    action: 'start' | 'pause' | 'resume' | 'complete' | 'skip'
  ): string {
    if (err instanceof HttpErrorResponse) {
      const serverMessage = extractApiErrorMessage(err);
      if (serverMessage) {
        return serverMessage;
      }
      if (err.status === 409) {
        return action === 'start'
          ? 'Another running block already exists.'
          : 'This block has already changed. Refresh and retry.';
      }
    }
    return describeGenericApiError(err, `Failed to ${action} block`);
  }

  private resolveScheduleType(fromParam: string | null): ScheduleType {
    if (!fromParam) {
      return 'PRIMARY';
    }
    const normalized = fromParam.trim().toUpperCase();
    if (normalized === 'PRIMARY'
      || normalized === 'SECONDARY'
      || normalized === 'TERTIARY'
      || normalized === 'QUATERNARY'
      || normalized === 'QUINARY') {
      return normalized;
    }
    return scheduleTypeFromSlug(fromParam) ?? 'PRIMARY';
  }
}
